import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useDataProvider, useGetOne, useNotify, useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileContent } from "@/components/atomic-crm/layout/MobileContent";
import MobileHeader from "@/components/atomic-crm/layout/MobileHeader";
import { Markdown } from "@/components/atomic-crm/misc/Markdown";
import {
  keyFromName,
  type Procedure,
  useProcedures,
} from "@/tenax/tasks/taskData";

// Procedures (task 1.3, D-030): short Markdown texts, written by the team
// (D-018) and opened from a task with one tap. Rendering goes through
// Atomic's Markdown component, which sanitizes the HTML.

export const PageFrame = ({
  title,
  back,
  children,
}: {
  title: string;
  back?: React.ReactNode;
  children: React.ReactNode;
}) => {
  const isMobile = useIsMobile();
  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <div className="flex min-w-0 items-center gap-1">
            {back}
            <h1 className="truncate text-lg font-semibold">{title}</h1>
          </div>
        </MobileHeader>
        <MobileContent>{children}</MobileContent>
      </>
    );
  }
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 py-6">
      <div className="flex items-center gap-2">
        {back}
        <h1 className="text-2xl font-semibold">{title}</h1>
      </div>
      {children}
    </div>
  );
};

export const ProcedureList = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const { data: procedures = [], isPending } = useProcedures();
  const [newTitle, setNewTitle] = useState("");

  const add = async (event: React.FormEvent) => {
    event.preventDefault();
    const title = newTitle.trim();
    if (!title) return;
    try {
      const { data } = await dataProvider.create<Procedure>("procedures", {
        data: { key: keyFromName(title), title, body_md: "", scope: "process" },
      });
      await queryClient.invalidateQueries({ queryKey: ["procedures"] });
      navigate(`/procedures/${data.id}/show`);
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), {
        type: "error",
      });
    }
  };

  return (
    <PageFrame title={translate("tenax.tasks.procedures.title")}>
      <div className="flex flex-col gap-4">
        {!isPending && procedures.length === 0 && (
          <p className="text-muted-foreground text-sm">
            {translate("tenax.tasks.procedures.empty")}
          </p>
        )}
        <ul className="flex flex-col divide-y rounded-md border">
          {procedures.map((procedure) => (
            <li key={procedure.id}>
              <Link
                to={`/procedures/${procedure.id}/show`}
                className="block px-3 py-3 font-medium"
              >
                {procedure.title}
              </Link>
            </li>
          ))}
        </ul>
        <form onSubmit={add} className="flex gap-2">
          <Input
            value={newTitle}
            placeholder={translate("tenax.tasks.procedures.new_title")}
            aria-label={translate("tenax.tasks.procedures.new_title")}
            onChange={(event) => setNewTitle(event.target.value)}
          />
          <Button type="submit" disabled={!newTitle.trim()}>
            {translate("tenax.tasks.procedures.add")}
          </Button>
        </form>
      </div>
    </PageFrame>
  );
};

export const ProcedureShow = () => {
  const { id } = useParams();
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const { data: procedure } = useGetOne<Procedure>("procedures", {
    id: Number(id),
  });
  const [draft, setDraft] = useState<{ title: string; body: string } | null>(
    null,
  );

  if (!procedure) return null;

  const save = async () => {
    if (!draft) return;
    try {
      await dataProvider.update("procedures", {
        id: procedure.id,
        data: {
          title: draft.title.trim() || procedure.title,
          body_md: draft.body,
        },
        previousData: procedure,
      });
      await queryClient.invalidateQueries({ queryKey: ["procedures"] });
      notify("tenax.tasks.procedures.saved", { type: "success" });
      setDraft(null);
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), {
        type: "error",
      });
    }
  };

  const back = (
    <Button
      asChild
      variant="ghost"
      size="icon"
      aria-label={translate("tenax.tasks.procedures.back")}
    >
      <Link to="/procedures">
        <ArrowLeft />
      </Link>
    </Button>
  );

  return (
    <PageFrame title={procedure.title} back={back}>
      {draft ? (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="procedure-title">
              {translate("tenax.tasks.fields.title")}
            </Label>
            <Input
              id="procedure-title"
              value={draft.title}
              onChange={(event) =>
                setDraft({ ...draft, title: event.target.value })
              }
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="procedure-body">
              {translate("tenax.tasks.procedures.body")}
            </Label>
            <Textarea
              id="procedure-body"
              rows={14}
              value={draft.body}
              onChange={(event) =>
                setDraft({ ...draft, body: event.target.value })
              }
            />
          </div>
          <h2 className="text-muted-foreground text-sm font-semibold uppercase">
            {translate("tenax.tasks.procedures.preview")}
          </h2>
          <div className="rounded-md border p-3">
            <Markdown>{draft.body}</Markdown>
          </div>
          <div className="flex gap-2">
            <Button onClick={save}>{translate("ra.action.save")}</Button>
            <Button variant="outline" onClick={() => setDraft(null)}>
              {translate("ra.action.cancel")}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <ProcedureBody procedure={procedure} />
          <Button
            variant="outline"
            className="self-start"
            onClick={() =>
              setDraft({ title: procedure.title, body: procedure.body_md })
            }
          >
            {translate("tenax.tasks.procedures.edit")}
          </Button>
        </div>
      )}
    </PageFrame>
  );
};

export const ProcedureBody = ({ procedure }: { procedure: Procedure }) => {
  const translate = useTranslate();
  return procedure.body_md.trim() ? (
    <Markdown>{procedure.body_md}</Markdown>
  ) : (
    <p className="text-muted-foreground text-sm">
      {translate("tenax.tasks.procedures.empty_body")}
    </p>
  );
};
